/**
 * RefundBatch — THE admin initiation seam; a single refund is a batch of
 * one. Cross-event by nature (a whole-event cancellation, or an arbitrary
 * member selection), so it stays top-level rather than nesting under one
 * event.
 *
 * Path: `workspaces/{orgId}/refundBatches/{batchId}` — member create
 * (`tickets.orders.refund`).
 *
 * `workspaces/{orgId}/refundBatches/{batchId}/_orders/{orderId}` carries one
 * fire-once marker per distinct order in the selection (`RefundMarkerSchema`
 * below) — server-written only (the fan-out trigger); no client read/write
 * path exists for that subcollection, so it carries no policy of its own and
 * no `entity(...)` binding.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { z } from "zod";
import { PositiveCents } from "./ticket-type.entity.ts";
import type { Actor } from "./workspace.entity.ts";

// No `kind` discriminator: a whole-event cancellation is a *producer* of a
// selection, not a different document — nothing branches on it (no rule
// predicate, no index, no trigger branch), so it recorded provenance while
// implying two shapes exist. One shape exists.
//
// `progress` keeps its counters — a live progress bar cannot aggregate the
// `_orders` subcollection, which is the classic earned counter — but not
// `total`, which mirrored a length the same reader already holds. The
// invariant checks the true fan-out set: markers are keyed
// `_orders/{orderId}`, so a selection naming one order twice (a partial AND
// a per-ticket refund) produces ONE marker, and the bound is the count of
// DISTINCT orderIds, not the selection's length.
export const RefundBatchSchema = z
	.object({
		selection: z
			.array(
				z.object({
					eventId: z
						.string()
						.describe(
							"the order’s owning event — orders nest under their owning event, so the fan-out trigger needs this field to address the order it is refunding; refundBatches/{batchId} itself sits outside any event’s tree, so this is the only place the fan-out trigger can read it from",
						),
					orderId: z.string(),
					ticketIds: z
						.array(z.string())
						.min(1)
						.nullish()
						.describe("absent means the whole order"),
					amount: PositiveCents.nullish().describe(
						"a partial amount — absent means the selected lines’ full amounts. Positive, because an entry asking for nothing back is not a refund of zero: an order the operator does not mean to refund is left out of the selection, and a zero here reached the consumer as a live instruction that gave no money back while leaving every named ticket admitting at the door",
					),
				}),
			)
			.min(1),
		createdBy: z.string(),
		progress: z.object({
			done: z.number().int().min(0).default(0),
			failed: z.number().int().min(0).default(0),
		}),
	})
	.refine(
		(b) =>
			b.progress.done + b.progress.failed <=
			new Set(b.selection.map((s) => s.orderId)).size,
		{ message: "progress cannot exceed the batch’s distinct orders" },
	);
export type RefundBatch = z.infer<typeof RefundBatchSchema>;

export const RefundBatch = entity<Actor>()({
	schema: RefundBatchSchema,
	actions: ["read", "create"] as const,
})
	.pipe(({ can, hasPermission, and }) => [
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.reports.view"),
				me.mfa.eq(true),
			),
		),
		// Selection + amounts are drawn from the order's immutable lines by the
		// refundBatch trigger, not validated here — the AST has no cross-doc
		// read to compare against a live order; the coarse permission gate is
		// what the rules layer can express. createdBy is pinned to the caller.
		can("create")
			.when((data, me) =>
				and(
					hasPermission(me.permissions, "tickets.orders.refund"),
					me.mfa.eq(true),
					data.createdBy.eq(me.uid),
				),
			)
			.writes((w) => w.set({ createdBy: "me.uid" })),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/refundBatches/{batchId}",
		}),
	);

// ── The fan-out marker ─────────────────────────────────────────────────────
// `pending` until the consumer runs; `done` and `failed` are the batch's own
// `progress` counter keys above, so the two vocabularies are one and the
// consumer increments the counter its own terminal status names. Deliberately
// not DeliveryTaskStatusSchema (order.entity.ts): a marker's lifecycle is
// bound to this document's parent, not to the fulfillment task ladder that
// happens to spell its three tokens the same way.
export const RefundMarkerStatusSchema = z
	.enum(["pending", "done", "failed"])
	.describe(
		"the marker's own consume-once lifecycle — pending until the consumer runs it, then done or failed, and the terminal token names the batch progress counter it increments",
	);

// The first four are the consumer's refusals, each one whole: no money moves
// and no ticket is touched. A single unanswered PSP call is NOT one of these —
// it leaves the marker `pending` and increments `attempts`, so the poll retries
// under the unchanged idempotency key.
//
// `alreadyRefunded` is the one refusal that leaves the marker's named tickets
// STILL LIVE at the door: the order gave its whole gross back already, so no
// cents can follow these tickets, and no retry revises that — `refundedAmount`
// has one direction and every writer of it only ever raises it. It is terminal
// rather than retryable for exactly that reason, and it is `failed` rather than
// `done` because nothing came back for the tickets it names.
//
// The last two end the retry ladder, and they are the reason a marker can be
// terminal while money may already have left: `stranded` says this marker's
// own ledger row is `failed` and an operator must answer for it — the row's
// own `failure` says whether the provider may be holding the money or
// rejected the refund against the order itself — `attemptsExhausted` says the
// ladder ran out with no claim ever taken, so nothing moved. Neither is silent — the row a `stranded`
// marker names is what refundPoll's failed-refund leg alarms on.
export const RefundMarkerFailureSchema = z
	.enum([
		"notPaid",
		"redeemedTicket",
		"lineTicketMismatch",
		"alreadyRefunded",
		"stranded",
		"attemptsExhausted",
	])
	.describe(
		"why the consumer gave up on this marker — notPaid when the order does not hold a captured payment to give back, redeemedTicket when the named set contains a ticket the door already admitted, lineTicketMismatch when a line's ticket count disagrees with its qty so no per-ticket share is derivable, alreadyRefunded when the order has already given back its whole gross while the tickets this marker names are still valid, stranded when this marker's ledger row is left failed for an operator — the row's own failure says whether the provider may be holding the money or rejected the refund against the order itself, attemptsExhausted when the retry ladder ran out with no claim ever taken",
	);

// The fan-out's per-order work record, at
// `refundBatches/{batchId}/_orders/{orderId}` — one document per distinct
// order id in the selection, merged per the batch's own merge rule. A bare
// schema with no `entity(...)` binding (the precedent OrderLineSchema sets):
// the subcollection is server-written under default-deny and has no client
// path at all, so there is no action to gate and no rule to emit.
//
// Not DeliveryTaskSchema: that schema's `task` enum names fulfillment
// artifacts only, and its sendNumber/providerMessageId/deliveryStatus are mail
// concepts a refund has no use for.
//
// Fields justified:
//   eventId          — orders nest under their own event and this marker's
//                      path names none, so it is the only place the consumer
//                      can read the order's address from
//   ticketIds        — the refund unit is the ticket, not the order
//   amount           — the operator override the consumer clamps
//   status           — the consume-once lifecycle, and the counter it feeds
//   attempts         — what the poll's retry bound is measured against
//   refundId         — the ledger row this marker produced, held here so the
//                      link is a path read rather than a collection-group
//                      query on batchId. NOT the provider's refund id: the
//                      ledger is keyed on a claim id minted before the
//                      provider is called (refund.entity.ts), and the
//                      provider's own id rides that row as a field
//   failure          — which refusal stranded this order, for the operator
//                      reading logs and OPERATIONS.md. No client renders it:
//                      the subcollection is server-written under default-deny
//                      with no emitted rule, so a console sees only the
//                      batch's own progress counters.
// Excluded:
//   orderId    — the marker's own doc id
//   batchId    — the marker's own parent path
//   the amount actually refunded — the ledger row at providerRefundId owns it,
//     and a copy here is free to contradict the money doc
//   createdAt / updatedAt — nothing queries or orders markers by time; the
//     batch's progress counters are the only aggregate any reader wants
export const RefundMarkerSchema = z.object({
	eventId: z
		.string()
		.describe(
			"the owning event of the order this marker refunds — orders nest under their event, and the marker's own path carries only the batch and the order",
		),
	ticketIds: z
		.array(z.string())
		.min(1)
		.nullish()
		.describe(
			"exactly the tickets to give back and cancel — absent means every ticket of the order",
		),
	amount: PositiveCents.nullish().describe(
		"an operator-chosen cap on what this order gives back — absent means the named tickets' share derived from the order's own immutable lines. Positive: a cap of zero is not a smaller refund, it is no refund, and the consumer answered it by stamping the marker done over tickets nothing had given money back for",
	),
	status: RefundMarkerStatusSchema.default("pending"),
	attempts: z
		.number()
		.int()
		.min(0)
		.default(0)
		.describe(
			"how many times the consumer has reached for the provider on this marker — every attempt carries the same idempotency key, so the count bounds the retry ladder without ever risking a second refund",
		),
	refundId: z
		.string()
		.nullish()
		.describe(
			"the id of the ledger row this marker produced, written once that row settles — a claim id derived from the refunded ticket set, not the provider's own refund id, which rides the ledger row itself",
		),
	failure: RefundMarkerFailureSchema.nullish().describe(
		"why the consumer refused this marker — absent while the marker is pending or done",
	),
});
export type RefundMarker = z.infer<typeof RefundMarkerSchema>;
// z.input, not z.infer: status and attempts both carry a .default(), so the
// fan-out's own tx.create literal hands the schema a looser shape than a parse
// returns — and it persists what .parse() gives back, never the raw literal.
export type RefundMarkerInput = z.input<typeof RefundMarkerSchema>;
